import React, { useState, useEffect } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import Cookies from "js-cookie";
import { adminMainUrl } from "../../../Config.jsx";
import { quotationMetrics as fallbackMetrics, quotationChartData as fallbackChart, quotationExpiry as fallbackExpiry } from "./dashboardData";
import DashIcon from "./DashIcon";

const QuotationOverviewCard = () => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const fetchQuotationData = async () => {
      const token = Cookies.get('token');
      const user_id = Cookies.get('flexichain_user_id');
      try {
        const response = await fetch(`${adminMainUrl}/api/operation-dashboard-quotation-data?filter=${filter}`, {
          method: "GET",
          headers: {
            'auth-token': token || '',
            'Authorization': token ? (token.startsWith('Bearer ') ? token : `Bearer ${token}`) : '',
            'user_id': user_id || '',
            'user-id': user_id || '',
            'Content-Type': 'application/json',
          }
        });
        const resData = await response.json();
        if (response.ok && resData?.code === 200) {
          setData(resData.data);
        }
      } catch (err) {
        console.error("Error fetching quotation data:", err);
      }
    };

    fetchQuotationData();
  }, [filter]);

  const metrics = data?.quotationOverview ? [
    { label: "Created", value: data.quotationOverview.created },
    { label: "Sent", value: data.quotationOverview.sent },
    { label: "Accepted", value: data.quotationOverview.accepted },
    { label: "Conversion Rate", value: `${data.quotationOverview.conversionRate}%` },
  ] : fallbackMetrics;

  const chartData = data?.quotationChartData?.length > 0 ? data.quotationChartData : fallbackChart;

  const expiry = data?.expiringStatus ? [
    { label: "Expiring Today", value: data.expiringStatus.expiringToday, color: "#EA580C" },
    { label: "Expiring in 3 Days", value: data.expiringStatus.expiringIn3Days, color: "#D97706" },
    { label: "Expired", value: data.expiringStatus.expired, color: "#DC2626" },
  ] : fallbackExpiry;

  return (
    <div className="ops-card ops-quotation-card">
      {/* Header */}
      <div className="ops-card-header">
        <div className="ops-card-heading">
          <span className="ops-card-heading-icon ops-heading-quotation">
            <DashIcon type="quotation" size={17} color="#fff" />
          </span>
          <span className="ops-card-title">Quotation Overview</span>
        </div>
        <select
          className="ops-dropdown"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="all">All</option>
          <option value="this_week">This Week</option>
          <option value="last_week">Last Week</option>
          <option value="this_month">This Month</option>
        </select>
      </div>

      {/* Summary Metrics */}
      <div className="ops-quot-metrics">
        {metrics.map((m) => (
          <div key={m.label} className="ops-quot-metric">
            <span className="ops-quot-metric-label">{m.label}</span>
            <span className="ops-quot-metric-value">{m.value}</span>
          </div>
        ))}
      </div>

      {/* Chart + Expiry side-by-side */}
      <div className="ops-quot-body">
        {/* Line/Area Chart */}
        <div className="ops-quot-chart">
          <ResponsiveContainer width="100%" height={150}>
            <AreaChart
              data={chartData}
              margin={{ top: 5, right: 10, left: -20, bottom: 0 }}
            >
              <defs>
                <linearGradient id="quotGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563EB" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#2563EB" stopOpacity={0.01} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: "#94A3B8" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                domain={[0, 40]}
                tick={{ fontSize: 11, fill: "#94A3B8" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  border: "1px solid #E2E8F0",
                  fontSize: 12,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
                cursor={{ stroke: "#2563EB", strokeWidth: 1, strokeDasharray: "3 3" }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#2563EB"
                strokeWidth={2}
                fill="url(#quotGradient)"
                dot={{ r: 4, fill: "#fff", stroke: "#2563EB", strokeWidth: 2 }}
                activeDot={{ r: 6, fill: "#2563EB" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Expiry Summary */}
        <div className="ops-quot-expiry">
          {expiry.map((e) => (
            <div key={e.label} className="ops-quot-expiry-row">
              <span
                className="ops-quot-expiry-icon"
                style={{ background: `${e.color}18` }}
              >
                <DashIcon
                  type={e.label === "Expired" ? "warning" : "calendar"}
                  size={16}
                  color={e.color}
                />
              </span>
              <div className="ops-quot-expiry-info">
                <span className="ops-quot-expiry-label">{e.label}</span>
                <span className="ops-quot-expiry-value" style={{ color: e.color }}>
                  {e.value}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QuotationOverviewCard;
